import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, User, Bot, ArrowLeft, Sparkles, Phone, MessageSquare } from "lucide-react";

import { toast } from "sonner";
import SmartHelpWidget from "@/components/widgets/SmartHelpWidget";
import { useWidgetTracking, detectUserIntent } from "@/hooks/useWidgetTracking";
import { supabase } from "@/integrations/supabase/client";
import { useIsFloatingSlotAllowed } from "@/contexts/FloatingCtaContext";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  showTransferOption?: boolean;
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chatbot-ai`;
const WHATSAPP_NUMBER = "41797700342";
const WHATSAPP_MESSAGE = "Hola, he visto su página web y necesito más información";
const PHONE_NUMBER = "+41 79 770 03 42";

const QUICK_PROMPTS = [
  "Permiso B/C/L: ¿qué necesito?",
  "Seguro LAMal obligatorio",
  "Inscripción RAV (desempleo)",
  "Clases de alemán",
  "Pack Relocation",
  "Consulta gratuita 10 min",
];

// Get personalized welcome message based on selected service
const getWelcomeMessage = (): string => {
  const service = sessionStorage.getItem("selected_service");
  const messages: Record<string, string> = {
    gestoria: "¡Hola! 👋 Veo que te interesan nuestros servicios de gestoría. ¿En qué puedo ayudarte?",
    "asesoria-legal": "¡Hola! 👋 Te ayudo con consultas de asesoría legal. ¿Qué necesitas saber?",
    legal: "¡Hola! 👋 Te ayudo con consultas de asesoría legal. ¿Qué necesitas saber?",
    seguros: "¡Hola! 👋 ¿Necesitas información sobre seguros en Suiza? Te ayudo a comparar opciones.",
    idiomas: "¡Hola! 👋 ¿Te interesa aprender alemán o inglés? Te cuento sobre nuestros cursos.",
    vivienda: "¡Hola! 👋 ¿Buscas alojamiento en Zürich? Te ayudo a encontrar opciones.",
    relocation: "¡Hola! 👋 ¿Acabas de llegar a Suiza? Te cuento sobre nuestros packs de bienvenida.",
    digital: "¡Hola! 👋 ¿Necesitas soluciones digitales para tu empresa? Web, CRM, IA... ¡Te ayudo!",
    desempleo: "¡Hola! 👋 ¿Necesitas ayuda con la inscripción en el RAV? Te explico el proceso.",
    rav: "¡Hola! 👋 ¿Necesitas ayuda con la inscripción en el RAV? Te explico el proceso.",
    retorno: "¡Hola! 👋 ¿Planeas volver a tu país? Te ayudo con las gestiones de retorno.",
    coches: "¡Hola! 👋 ¿Necesitas alquilar un vehículo o furgoneta? Te cuento las opciones.",
    traducciones: "¡Hola! 👋 ¿Necesitas traducir documentos? Trabajamos con alemán, inglés, francés, italiano y portugués.",
    cv: "¡Hola! 👋 ¿Necesitas crear o actualizar tu CV al formato suizo? ¡Te ayudo!",
  };
  return messages[service || ""] || "¡Hola! 👋 Soy el asistente de Punto Hispano. ¿En qué puedo ayudarte hoy?";
};

interface LeadFormData {
  name: string;
  contact: string;
  service: string;
}

const SERVICE_OPTIONS = [
  { value: "gestoria", label: "Gestoría y Asesoría (RAV, impuestos, trámites)" },
  { value: "legal", label: "Asesoría Legal (extranjería, laboral, familia)" },
  { value: "idiomas", label: "Formación en Idiomas (alemán, inglés, francés)" },
  { value: "viviendas", label: "Alquiler de Viviendas" },
  { value: "coches", label: "Alquiler de Vehículos" },
  { value: "seguros", label: "Seguros y Pensiones" },
  { value: "relocation", label: "Packs Relocation" },
  { value: "otro", label: "Otro / Solo consulta" },
];

const PublicChatbot = () => {
  const location = useLocation();
  const [showWidget, setShowWidget] = useState(true);
  const [isChatOpen, setIsChatOpen] = useState(false);
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showAgentTransfer, setShowAgentTransfer] = useState(false);
  const [detectedTopics, setDetectedTopics] = useState<string[]>([]);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadData, setLeadData] = useState<LeadFormData>({ name: "", contact: "", service: "" });
  const [leadCaptured, setLeadCaptured] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const conversationCreated = useRef(false);

  // Tracking hook integration
  const {
    createConversation,
    saveMessage,
    closeConversation,
    trackQuickReplyClick,
    trackEvent,
    generateUUID,
  } = useWidgetTracking();

  // Hide on PHCRM routes (own DocumentChatbot) and on private/auth/checkout routes
  const isAdminRoute = location.pathname.startsWith("/phcrm");
  const chatbotAllowed = useIsFloatingSlotAllowed("chatbot");

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    if (isChatOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isChatOpen]);

  // Initialize conversation with welcome message
  const initializeConversation = useCallback(async () => {
    if (conversationCreated.current) return;
    
    // Create conversation in backend
    await createConversation("chat-ia");
    conversationCreated.current = true;
    
    // Set initial welcome message
    const welcomeMessage: Message = {
      id: generateUUID(),
      role: "assistant",
      content: getWelcomeMessage(),
      timestamp: new Date().toISOString(),
    };
    setMessages([welcomeMessage]);
  }, [createConversation, generateUUID]);

  const handleOpenWhatsApp = useCallback(() => {
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`, "_blank");
  }, []);

  const handleLeadSubmit = useCallback(async () => {
    if (!leadData.name.trim() || !leadData.contact.trim()) return;
    
    // Save lead data to customer_interactions
    try {
      const isEmail = leadData.contact.includes("@");
      await supabase.from("customer_interactions").insert({
        interaction_type: "chat_web",
        customer_name: leadData.name.trim(),
        customer_email: isEmail ? leadData.contact.trim() : null,
        customer_phone: !isEmail ? leadData.contact.trim() : null,
        source_page: window.location.pathname,
        status: "new",
        metadata: { service_interest: leadData.service || "otro", source: "ai_chat_widget" },
      });
    } catch (err) {
      console.error("Error saving lead:", err);
    }
    
    setLeadCaptured(true);
    setShowLeadForm(false);
    setShowWidget(false);
    setIsChatOpen(true);
    await initializeConversation();
  }, [leadData, initializeConversation]);

  const handleOpenAIChat = useCallback(async () => {
    if (!leadCaptured) {
      setShowLeadForm(true);
      return;
    }
    setShowWidget(false);
    setIsChatOpen(true);
    await initializeConversation();
  }, [initializeConversation, leadCaptured]);


  const handleBackToOptions = useCallback(async () => {
    // Close conversation when going back
    await closeConversation(messages.length);
    conversationCreated.current = false;
    
    setIsChatOpen(false);
    setShowWidget(true);
    setMessages([]);
  }, [closeConversation, messages.length]);

  const handleCloseChat = useCallback(async () => {
    await closeConversation(messages.length);
    conversationCreated.current = false;
    
    setIsChatOpen(false);
    setShowWidget(true);
    setMessages([]);
  }, [closeConversation, messages.length]);


  const streamChat = useCallback(async (chatMessages: { role: string; content: string }[]) => {
    const resp = await fetch(CHAT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify({ messages: chatMessages }),
    });

    if (!resp.ok) {
      const errorData = await resp.json().catch(() => ({}));
      throw new Error(errorData.error || "Error al conectar con el asistente");
    }

    if (!resp.body) throw new Error("No response body");

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();

    let textBuffer = "";
    let assistantContent = "";
    let pending = "";
    let streamDone = false;

    const botMessageId = generateUUID();

    const upsertAssistant = (nextText: string) => {
      assistantContent = nextText;
      setMessages((prev) => {
        const lastMsg = prev[prev.length - 1];
        if (lastMsg?.role === "assistant" && lastMsg.id === botMessageId) {
          return prev.map((m) => (m.id === botMessageId ? { ...m, content: assistantContent } : m));
        }
        return [
          ...prev,
          {
            id: botMessageId,
            role: "assistant",
            content: assistantContent,
            timestamp: new Date().toISOString(),
          },
        ];
      });
    };

    // Throttle UI updates to avoid jank on token-by-token streams
    let flushTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleFlush = () => {
      if (flushTimer) return;
      flushTimer = setTimeout(() => {
        flushTimer = null;
        if (!pending) return;
        assistantContent += pending;
        pending = "";
        upsertAssistant(assistantContent);
      }, 50);
    };

    while (!streamDone) {
      const { done, value } = await reader.read();
      if (done) break;

      textBuffer += decoder.decode(value, { stream: true });

      let newlineIndex: number;
      while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
        let line = textBuffer.slice(0, newlineIndex);
        textBuffer = textBuffer.slice(newlineIndex + 1);

        if (line.endsWith("\r")) line = line.slice(0, -1);
        if (line.startsWith(":") || line.trim() === "") continue;
        if (!line.startsWith("data: ")) continue;

        const jsonStr = line.slice(6).trim();
        if (jsonStr === "[DONE]") {
          streamDone = true;
          break;
        }

        try {
          const parsed = JSON.parse(jsonStr);
          const content = parsed.choices?.[0]?.delta?.content as string | undefined;
          if (content) {
            pending += content;
            scheduleFlush();
          }
        } catch {
          // Incomplete JSON split across chunks: put it back and wait for more data
          textBuffer = line + "\n" + textBuffer;
          break;
        }
      }
    }

    // Final flush (tokens + UI)
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    if (pending) {
      assistantContent += pending;
      pending = "";
    }
    
    // Fallback if stream returned empty (model consumed tokens on reasoning)
    if (!assistantContent.trim()) {
      assistantContent = "Lo siento, estoy teniendo dificultades para responder. ¿Prefieres que te conecte con un agente? Puedes contactarnos al +41 79 770 03 42 o por WhatsApp.";
    }
    
    upsertAssistant(assistantContent);

    // Save bot response to backend (no bloquea la UI)
    void saveMessage({
      id: botMessageId,
      sender: "bot",
      text: assistantContent,
      timestamp: new Date().toISOString(),
    });

    return assistantContent;
  }, [generateUUID, saveMessage]);

  const handleSend = async (override?: string) => {
    const text = (override ?? input).trim();
    if (!text || isLoading) return;

    // Detect intent for analytics and agent transfer
    const intent = detectUserIntent(text);
    if (intent.topics.length > 0) {
      setDetectedTopics(prev => [...new Set([...prev, ...intent.topics])]);
    }
    if (intent.needsAgent) {
      setShowAgentTransfer(true);
    }

    const userMessageId = generateUUID();
    const userMessage: Message = { 
      id: userMessageId,
      role: "user", 
      content: text,
      timestamp: new Date().toISOString()
    };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);
    setIsTyping(true);

    // Save user message to backend (no bloquea la respuesta del bot)
    void saveMessage({
      id: userMessageId,
      sender: "user",
      text,
      timestamp: new Date().toISOString(),
      metadata: { topics: intent.topics, needsAgent: intent.needsAgent, isHighValue: intent.isHighValue },
    });

    // Track high value leads (no bloquea la respuesta del bot)
    if (intent.isHighValue) {
      void trackEvent({
        event_type: "high_value_intent",
        properties: { topics: intent.topics, message: text.substring(0, 100) },
      });
    }

    try {
      const chatMessages = newMessages.map(m => ({ role: m.role, content: m.content }));
      await streamChat(chatMessages);
    } catch (error) {
      console.error("Chat error:", error);
      toast.error(error instanceof Error ? error.message : "Error al enviar mensaje");
      
      const errorMessageId = generateUUID();
      const errorMessage: Message = {
        id: errorMessageId,
        role: "assistant",
        content: "Lo siento, hubo un error. Puedes contactarnos directamente al +41 79 770 03 42.",
        timestamp: new Date().toISOString()
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const handleTransferToAgent = () => {
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hola, necesito hablar con un agente")}`, "_blank");
    trackEvent({ event_type: "agent_transfer_clicked", properties: { topics: detectedTopics } });
  };

  const handleCallAgent = () => {
    window.location.href = `tel:${PHONE_NUMBER.replace(/\s/g, "")}`;
    trackEvent({ event_type: "call_agent_clicked", properties: { topics: detectedTopics } });
  };

  const handleQuickPromptClick = async (prompt: string) => {
    await trackQuickReplyClick(prompt);
    await handleSend(prompt);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (isAdminRoute || !chatbotAllowed) {
    return null;
  }

  return (
    <>
      <style>{`
        /* AI Chat Window Styles */
        .ai-chat-window {
          position: fixed;
          z-index: 10000;
          display: flex;
          flex-direction: column;
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
        }

        @media (max-width: 640px) {
          .ai-chat-window {
            bottom: 16px;
            right: 16px;
            width: calc(100vw - 32px);
            max-width: 340px;
            height: 500px;
            max-height: calc(100vh - 100px);
          }
        }

        @media (min-width: 641px) {
          .ai-chat-window {
            bottom: 24px;
            right: 24px;
            width: 400px;
            height: 600px;
            max-height: calc(100vh - 100px);
          }
        }

        @media (min-width: 1024px) {
          .ai-chat-window {
            bottom: 32px;
            right: 32px;
          }
        }

        .ai-chat-header {
          padding: 16px 20px;
          background: linear-gradient(135deg, #FEF2F2 0%, #FFFFFF 100%);
          border-bottom: 1px solid #f0f0f0;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .ai-chat-header-left {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .ai-chat-back-btn {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          background: rgba(185, 28, 28, 0.1);
          color: #B91C1C;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          border: none;
          transition: all 0.2s ease;
        }

        .ai-chat-back-btn:hover {
          background: rgba(185, 28, 28, 0.2);
        }

        .ai-chat-header-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          overflow: hidden;
          background: #B91C1C;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .ai-chat-header-text h3 {
          font-size: 15px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0;
        }

        .ai-chat-header-text .status {
          font-size: 12px;
          color: #666;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .ai-chat-header-text .status .online-dot {
          width: 6px;
          height: 6px;
          background: #22c55e;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .ai-chat-close-btn {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: transparent;
          color: #999;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          border: none;
          transition: all 0.2s ease;
        }

        .ai-chat-close-btn:hover {
          background: #f5f5f5;
          color: #333;
        }

        .ai-chat-messages {
          flex: 1;
          overflow-y: auto;
          padding: 20px;
          background: #FAFAFA;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .ai-chat-message {
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }

        .ai-chat-message.user {
          flex-direction: row-reverse;
        }

        .ai-chat-message-icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .ai-chat-message-icon.bot {
          background: #B91C1C;
          color: white;
        }

        .ai-chat-message-icon.user {
          background: #e5e5e5;
          color: #666;
        }

        .ai-chat-bubble {
          max-width: 75%;
          padding: 12px 16px;
          border-radius: 12px 12px 12px 4px;
          font-size: 14px;
          line-height: 1.5;
        }

        .ai-chat-bubble.bot {
          background: white;
          color: #333;
          box-shadow: 0 2px 4px rgba(0,0,0,0.06);
        }

        .ai-chat-bubble.user {
          background: #B91C1C;
          color: white;
          border-radius: 12px 12px 4px 12px;
        }

        .ai-chat-quick-prompts {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 12px;
        }

        .ai-chat-quick-btn {
          padding: 10px 16px;
          background: white;
          border: 1px solid #e5e5e5;
          border-radius: 20px;
          font-size: 13px;
          color: #333;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .ai-chat-quick-btn:hover {
          border-color: #B91C1C;
          background: rgba(185, 28, 28, 0.05);
          color: #B91C1C;
        }

        .ai-chat-typing {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: white;
          border-radius: 12px 12px 12px 4px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.06);
        }

        .ai-chat-typing-label {
          font-size: 13px;
          color: #666;
          font-weight: 500;
        }

        .ai-chat-typing-dots {
          display: flex;
          align-items: center;
          gap: 3px;
        }

        .ai-chat-typing-dot {
          width: 6px;
          height: 6px;
          background: #B91C1C;
          border-radius: 50%;
          animation: typingPulse 1.2s ease-in-out infinite;
        }

        .ai-chat-typing-dot:nth-child(1) { animation-delay: 0s; }
        .ai-chat-typing-dot:nth-child(2) { animation-delay: 0.15s; }
        .ai-chat-typing-dot:nth-child(3) { animation-delay: 0.3s; }

        @keyframes typingPulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }

        .ai-chat-input-area {
          padding: 16px 20px;
          border-top: 1px solid #e5e5e5;
          background: white;
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .ai-chat-input {
          flex: 1;
          padding: 12px 16px;
          border: 1px solid #e5e5e5;
          border-radius: 24px;
          font-size: 14px;
          outline: none;
          transition: all 0.2s ease;
        }

        .ai-chat-input:focus {
          border-color: #B91C1C;
          box-shadow: 0 0 0 3px rgba(185, 28, 28, 0.1);
        }

        .ai-chat-input::placeholder {
          color: #999;
        }

        .ai-chat-send-btn {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #B91C1C;
          color: white;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .ai-chat-send-btn:hover:not(:disabled) {
          background: #9A1818;
          transform: scale(1.05);
        }

        .ai-chat-send-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .ai-chat-footer {
          padding: 8px;
          text-align: center;
          font-size: 11px;
          color: #999;
          border-top: 1px solid #f5f5f5;
          background: white;
        }

        @media (prefers-color-scheme: dark) {
          .ai-chat-window {
            background: #1a1a1a;
          }
          .ai-chat-header {
            background: linear-gradient(135deg, rgba(185, 28, 28, 0.1) 0%, #1a1a1a 100%);
            border-bottom-color: #333;
          }
          .ai-chat-header-text h3 {
            color: #f5f5f5;
          }
          .ai-chat-header-text .status {
            color: #aaa;
          }
          .ai-chat-messages {
            background: #111;
          }
          .ai-chat-bubble.bot {
            background: #222;
            color: #f5f5f5;
          }
          .ai-chat-quick-btn {
            background: #222;
            border-color: #333;
            color: #f5f5f5;
          }
          .ai-chat-quick-btn:hover {
            background: rgba(185, 28, 28, 0.1);
          }
          .ai-chat-typing {
            background: #222;
          }
          .ai-chat-input-area {
            background: #1a1a1a;
            border-top-color: #333;
          }
          .ai-chat-input {
            background: #222;
            border-color: #333;
            color: #f5f5f5;
          }
          .ai-chat-footer {
            background: #1a1a1a;
            border-top-color: #333;
          }
        }
      `}</style>

      {/* Lead Capture Form Modal */}
      <AnimatePresence>
        {showLeadForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4"
            onClick={() => setShowLeadForm(false)}
          >
            <div className="fixed inset-0 bg-black/40" />
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4"
            >
              <button onClick={() => setShowLeadForm(false)} className="absolute top-3 right-3 p-1 hover:bg-slate-100 rounded-full">
                <X className="w-4 h-4 text-slate-400" />
              </button>
              <div className="text-center">
                <div className="text-3xl mb-2">💬</div>
                <h3 className="text-lg font-semibold text-slate-900">¡Hola! ¿En qué podemos ayudarte?</h3>
                <p className="text-sm text-slate-500 mt-1">Completa tus datos para iniciar la conversación</p>
              </div>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Tu nombre *"
                  value={leadData.name}
                  onChange={(e) => setLeadData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500"
                  style={{ fontSize: '16px' }}
                />
                <input
                  type="text"
                  placeholder="Email o Teléfono *"
                  value={leadData.contact}
                  onChange={(e) => setLeadData(prev => ({ ...prev, contact: e.target.value }))}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500"
                  style={{ fontSize: '16px' }}
                />
                <select
                  value={leadData.service}
                  onChange={(e) => setLeadData(prev => ({ ...prev, service: e.target.value }))}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 bg-white"
                  style={{ fontSize: '16px' }}
                >
                  <option value="">¿Qué servicio te interesa?</option>
                  {SERVICE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleLeadSubmit}
                disabled={!leadData.name.trim() || !leadData.contact.trim()}
                className="w-full py-3 bg-[#B91C1C] text-white rounded-xl font-medium text-sm hover:bg-[#991B1B] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Iniciar conversación →
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Smart Help Widget - replaces old floating button */}
      {showWidget && !isChatOpen && !showLeadForm && (
        <SmartHelpWidget
          onOpenAIChat={handleOpenAIChat}
          onOpenWhatsApp={handleOpenWhatsApp}
        />
      )}


      {/* AI Chat Window */}
      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            className="ai-chat-window"
          >
            {/* Header */}
            <div className="ai-chat-header">
              <div className="ai-chat-header-left">
                <button
                  onClick={handleBackToOptions}
                  className="ai-chat-back-btn"
                  aria-label="Volver al menú de opciones"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <div className="ai-chat-header-icon">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="ai-chat-header-text">
                  <h3>ASISTENTE IA</h3>
                  <span className="status">
                    <span className="online-dot" />
                    Online · Responde al instante
                  </span>
                </div>
              </div>
              <button
                onClick={handleCloseChat}
                className="ai-chat-close-btn"
                aria-label="Cerrar chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div className="ai-chat-messages">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`ai-chat-message ${message.role}`}
                >
                  <div className={`ai-chat-message-icon ${message.role === "assistant" ? "bot" : "user"}`}>
                    {message.role === "assistant" ? (
                      <Bot className="w-4 h-4" />
                    ) : (
                      <User className="w-4 h-4" />
                    )}
                  </div>
                  <div className={`ai-chat-bubble ${message.role === "assistant" ? "bot" : "user"}`}>
                    {message.content}
                  </div>
                </motion.div>
              ))}

              {/* Quick Prompts - only on first message */}
              {messages.length === 1 && messages[0].role === "assistant" && (
                <div className="ai-chat-quick-prompts">
                  {QUICK_PROMPTS.map((prompt) => (
                    <button
                      key={prompt}
                      onClick={() => handleQuickPromptClick(prompt)}
                      className="ai-chat-quick-btn"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              )}

              {/* Agent Transfer Offer */}
              {showAgentTransfer && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="ai-chat-agent-transfer"
                  style={{
                    background: 'linear-gradient(135deg, #FEF2F2 0%, #FFFFFF 100%)',
                    borderRadius: '12px',
                    padding: '16px',
                    marginTop: '12px',
                    border: '1px solid #FECACA',
                  }}
                >
                  <p style={{ fontSize: '14px', color: '#333', marginBottom: '12px', fontWeight: 500 }}>
                    ¿Prefieres hablar con una persona de nuestro equipo?
                  </p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={handleTransferToAgent}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '10px 16px',
                        background: '#25D366',
                        color: 'white',
                        border: 'none',
                        borderRadius: '20px',
                        fontSize: '13px',
                        cursor: 'pointer',
                        fontWeight: 500,
                      }}
                    >
                      <MessageSquare className="w-4 h-4" />
                      WhatsApp
                    </button>
                    <button
                      onClick={handleCallAgent}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '10px 16px',
                        background: '#B91C1C',
                        color: 'white',
                        border: 'none',
                        borderRadius: '20px',
                        fontSize: '13px',
                        cursor: 'pointer',
                        fontWeight: 500,
                      }}
                    >
                      <Phone className="w-4 h-4" />
                      Llamar
                    </button>
                    <button
                      onClick={() => setShowAgentTransfer(false)}
                      style={{
                        padding: '10px 16px',
                        background: 'transparent',
                        color: '#666',
                        border: '1px solid #e5e5e5',
                        borderRadius: '20px',
                        fontSize: '13px',
                        cursor: 'pointer',
                      }}
                    >
                      Continuar con IA
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Typing Indicator - "Escribiendo..." */}
              {isTyping && messages[messages.length - 1]?.role === "user" && (
                <div className="ai-chat-message" role="status" aria-live="polite">
                  <div className="ai-chat-message-icon bot">
                    <Bot className="w-4 h-4" />
                  </div>
                  <div className="ai-chat-typing">
                    <span className="ai-chat-typing-label">Escribiendo</span>
                    <div className="ai-chat-typing-dots">
                      <span className="ai-chat-typing-dot" />
                      <span className="ai-chat-typing-dot" />
                      <span className="ai-chat-typing-dot" />
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="ai-chat-input-area">
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Escribe tu mensaje..."
                disabled={isLoading}
                className="ai-chat-input"
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || isLoading}
                className="ai-chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>

            {/* Footer */}
            <div className="ai-chat-footer">
              Powered by Punto Hispano AI
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PublicChatbot;
